// Hoodi testnet block-number source for the demo footer's "Last Synced Block".
// Boots with one JSON-RPC call to a public Hoodi node, then extrapolates forward
// at the Ethereum block time so the footer counter ticks naturally without more
// network calls. If the boot fetch fails we fall back to a hardcoded anchor so
// the demo still looks alive — the fallback drifts from reality but stays in
// the right order of magnitude.

const HOODI_CHAIN_ID = 560048;
const HOODI_RPC_URL = 'https://ethereum-hoodi-rpc.publicnode.com';
const ETH_BLOCK_TIME_MS = 12_000;

// Fallback anchor: rough Hoodi block height as of 2026-04 at ~12s blocks since
// genesis (~Mar 2025). Only used when the boot RPC call fails.
const FALLBACK_BLOCK_NUM = 2_800_000;

interface BlockAnchor {
  blockNum: number;
  fetchedAt: number;
}

let anchor: BlockAnchor = {
  blockNum: FALLBACK_BLOCK_NUM,
  fetchedAt: Date.now(),
};

async function fetchLatestHoodiBlock(): Promise<number | undefined> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 4000);
  try {
    const res = await fetch(HOODI_RPC_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', method: 'eth_blockNumber', params: [], id: 1 }),
      signal: ctrl.signal,
    });
    if (!res.ok) return undefined;
    const json = await res.json();
    const hex = json?.result;
    if (typeof hex !== 'string') return undefined;
    const n = parseInt(hex, 16);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  } catch (e) {
    console.warn('[blockChain] hoodi rpc fetch failed, using fallback anchor', e);
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

export async function resolveBlockAnchor(): Promise<void> {
  const live = await fetchLatestHoodiBlock();
  if (live !== undefined) {
    anchor = { blockNum: live, fetchedAt: Date.now() };
  }
}

export function getCurrentBlockNum(): number {
  const elapsed = Date.now() - anchor.fetchedAt;
  return anchor.blockNum + Math.floor(elapsed / ETH_BLOCK_TIME_MS);
}

export function getChainId(): number {
  return HOODI_CHAIN_ID;
}
